import { createContext, useContext, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconEdit } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import type { Aircraft } from '../../api/types';
import { useSession } from '../../app/session';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { AircraftFormDrawer } from './AircraftFormDrawer';

const AircraftContext = createContext<Aircraft | null>(null);

/** The aircraft every tab below is about. */
export function useAircraft() {
  const aircraft = useContext(AircraftContext);
  if (!aircraft) throw new Error('useAircraft must be used inside the aircraft detail routes');
  return aircraft;
}

const TABS = [
  { value: 'overview', path: '', labelKey: 'common.summary' },
  { value: 'logbook', path: 'logbook', labelKey: 'nav.logbook' },
  { value: 'maintenance', path: 'maintenance', labelKey: 'nav.maintenance' },
  { value: 'counters', path: 'counters', labelKey: 'nav.counters' },
  { value: 'alerts', path: 'alerts', labelKey: 'nav.alerts' },
  { value: 'documents', path: 'documents', labelKey: 'nav.documents' },
] as const;

export function AircraftDetailLayout() {
  const { t } = useTranslation();
  const { org, registration } = useParams({ from: '/$org/aircraft/$registration' });
  const location = useLocation();
  const { can } = useSession();
  const [isFormOpen, setFormOpen] = useState(false);

  const { data: aircraft, isLoading } = useQuery({
    queryKey: ['aircraft', registration],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/aircraft/{registration}/', {
          params: { path: { registration } },
        }),
      ),
  });

  const airworthiness = useQuery({
    queryKey: ['aircraft', registration, 'airworthiness'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/aircraft/{registration}/airworthiness/', {
          params: { path: { registration } },
        }),
      ),
  });

  if (isLoading || !aircraft) {
    return (
      <Center mih={320}>
        <Loader />
      </Center>
    );
  }

  const base = `/${org}/aircraft/${registration}`;
  const pathSegment = location.pathname.startsWith(`${base}/`)
    ? location.pathname.slice(base.length + 1).split('/')[0]
    : '';
  const activeTab = pathSegment || 'overview';

  const blocking = airworthiness.data?.blocking_items ?? [];

  return (
    <AircraftContext.Provider value={aircraft}>
      <PageHeader
        title={
          <Group gap="sm" wrap="nowrap">
            <Avatar src={aircraft.photo} radius="sm" size={40} color="aero">
              {aircraft.registration.slice(-2)}
            </Avatar>
            <span className="identifier">{aircraft.registration}</span>
          </Group>
        }
        subtitle={`${aircraft.type_label}${
          aircraft.aircraft_class ? ` · ${aircraft.aircraft_class}` : ''
        }${aircraft.serial_number ? ` · ${t('aircraft.serialNumber')} ${aircraft.serial_number}` : ''}`}
        badge={
          <Group gap={6}>
            <StatusBadge kind="aircraft" value={aircraft.status} />
            {aircraft.competition_number ? (
              <Badge variant="outline" color="gray">
                {aircraft.competition_number}
              </Badge>
            ) : null}
          </Group>
        }
        actions={
          can('aircraft.change') ? (
            <Button
              variant="default"
              leftSection={<IconEdit size={16} />}
              onClick={() => setFormOpen(true)}
            >
              {t('action.edit')}
            </Button>
          ) : null
        }
      />

      {airworthiness.data && !airworthiness.data.is_airworthy ? (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title={t('aircraft.cannotFly')}
          mb="md"
        >
          <Stack gap={2}>
            {aircraft.grounded_reason ? <Text fz="sm">{aircraft.grounded_reason}</Text> : null}
            {blocking.map((item) => (
              <Text key={item.task} fz="sm">
                {item.task} — {t('maintenance.remaining')}: {item.remaining}
              </Text>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <Tabs value={activeTab} mb="md" keepMounted={false}>
        <Tabs.List>
          {TABS.map((tab) => (
            <Tabs.Tab
              key={tab.value}
              value={tab.value}
              component={Link}
              // @ts-expect-error Mantine passes unknown props through to the Link component.
              to={tab.path ? `${base}/${tab.path}` : base}
            >
              {t(tab.labelKey)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <Outlet />

      <AircraftFormDrawer
        opened={isFormOpen}
        onClose={() => setFormOpen(false)}
        aircraft={aircraft}
      />
    </AircraftContext.Provider>
  );
}
